import React, { FC, useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, Divider, Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useTable } from 'shared/hooks';
import { formatDate } from 'shared/functions';
import { theme } from 'theme';
import { Datatable } from 'ui';
import { SubscriptionRatingType, UserRatingStatus } from '../types';
import { getUserRatingStatusLog } from '../services/rsrService';

const useStyles = makeStyles({
  dialog: {
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    width: theme.spacing(90),
  },
  content: {
    paddingTop: 'unset',
    paddingBottom: 'unset',
    height: theme.spacing(45),
    // @ts-ignore
    overflowY: 'overlay',
  },
  heading: {
    marginBottom: theme.spacing(1),
  },
  column: {
    marginRight: theme.spacing(3),
  },
});

export type UserRatingStatusLogProps = {
  userId?: string;
  ratingId: string;
  open: boolean;
  onClose: () => void;
  ratingType: SubscriptionRatingType;
};

export const UserRatingStatusLog: FC<UserRatingStatusLogProps> = ({
  open,
  onClose,
  userId,
  ratingId,
  ratingType,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [ratingStatusLog, setRatingStatusLog] = useState<Array<UserRatingStatus>>([]);
  const [ratingStatusLogTotal, setRatingStatusLogTotal] = useState(0);

  const [isPending, setIsPending] = useState(false);

  const prepareTableData = (statusLog: Array<UserRatingStatus>) => {
    return statusLog.map((item) => [
      item.statusDate,
      item.status,
      [item.modifiedByName, item.modifiedByEmail, item.modifiedBy],
    ]);
  };

  useEffect(() => {
    fetchLog(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchLog = async (page, pageSize: number = 10) => {
    if (userId) {
      setIsPending(true);
      const resp = await getUserRatingStatusLog(ratingType, userId, ratingId, page, pageSize);
      setRatingStatusLog(resp.data);
      setRatingStatusLogTotal(resp.total);
      setIsPending(false);
    }
  };

  const dateRender = (statusDate: string) => formatDate(statusDate, 'dd/MM/yyyy HH:mm:ss');

  const modifiedByRender = ([modifiedByName, modifiedByEmail, modifiedBy]: string[]) => (
    <Typography> {modifiedByName || modifiedByEmail || modifiedBy} </Typography>
  );

  const initialColumns = useCallback(
    () => [
      {
        name: 'date',
        label: t('ADMIN.LOG.DATE'),
        options: {
          sort: false,
          customBodyRender: dateRender,
        },
      },
      {
        name: 'status',
        label: t('ADMIN.LOG.STATUS'),
        options: {
          sort: false,
        },
      },
      {
        name: 'modifiedBy',
        label: t('ADMIN.LOG.MODIFIED_BY'),
        options: {
          sort: false,
          customBodyRender: modifiedByRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
    paginationLimit: 10,
  } as any);

  useEffect(() => {
    fetchLog(pagination.page, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setRatingStatusLog([]);
      }}
      PaperProps={{ className: classes.dialog }}
    >
      <DialogTitle>{t('ADMIN.RSR.USER_RATINGS_LOG_TITLE')}</DialogTitle>
      <DialogContent>
        <Box>
          <Divider />
          <Datatable
            loading={isPending}
            columns={columns}
            pagination={pagination}
            data={prepareTableData(ratingStatusLog)}
            total={ratingStatusLogTotal}
            onTableChange={onTableChange}
            onSortChange={onSortChange}
            showSelectionExtension={false}
            selectableRows={false}
            removeShadow
            skipHeader
            rowsPerPageOptions={[10, 25, 50]}
          />
          <Divider />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
